import { Box } from "@mui/material";
import type { JSX } from "react";

import type { SalesOrgLeadDetail } from "@xop/dashboard-shared";

/* ── Print-only meta block (visible only on the printed copy) ──── */

export function PrintHeader({ lead }: { lead: SalesOrgLeadDetail }): JSX.Element {
  return (
    <Box className="print-only" sx={{ display: "none", borderBottom: "1px solid black", pb: 1.5 }}>
      <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700 }}>
        {lead.name}
      </h1>
      <p style={{ margin: "4px 0 0 0", fontSize: "0.85rem" }}>
        {lead.country || "—"} · {lead.vertical || "—"} ·{" "}
        {Math.round(lead.confidence * 100)}% confidence ·{" "}
        Heat: {lead.heat}
      </p>
      <p style={{ margin: "4px 0 0 0", fontSize: "0.75rem", color: "#444" }}>
        Generated by XOP Networks autonomous sales agent ·{" "}
        {new Date().toLocaleString()}
      </p>
    </Box>
  );
}

/* ── Print stylesheet ──────────────────────────────────────────── */

/**
 * This powers ONLY a plain browser Ctrl+P of the page. The Export-download and
 * Share-email PDFs no longer screenshot the DOM — they're drawn as a vector
 * document by `buildLeadPdf` (`leadPdf.ts`) — so the old `.capturing-pdf`
 * html2canvas styling is gone.
 *
 * `@media print` uses the classic "hide everything, reveal only `.lead-print`,
 * pull it to the top-left" trick. Hooks: `.no-print` hides controls (back /
 * copy / export / share / collapsible toggles / lifecycle bar); `.print-only`
 * reveals the print-meta header; `[data-print-expand] .print-collapsed-show`
 * forces collapsibles open.
 */
export function PrintStyles(): JSX.Element {
  return (
    <style>{`
      @media print {
        /* Hide everything */
        body * { visibility: hidden !important; }

        /* Reveal only the lead detail subtree */
        .lead-print, .lead-print * { visibility: visible !important; }

        /* Pull it to the top-left and let it fill the page */
        .lead-print {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          padding: 16px 20px !important;
          background: #fff !important;
          color: #111 !important;
        }

        /* Hide every interactive control inside the print area */
        .lead-print .no-print { display: none !important; }
        .lead-print .print-only { display: block !important; }
        .print-only { display: none; }

        /* Force collapsibles open on print */
        .lead-print [data-print-expand] .print-collapsed-show {
          display: grid !important;
        }

        /* Flatten dashboard color tokens so the PDF doesn't have
           dark-mode artifacts in printed form. */
        .lead-print, .lead-print * {
          background: transparent !important;
          color: #111 !important;
          box-shadow: none !important;
        }
        .lead-print a { color: #1d4ed8 !important; }

        /* Tighten typography for print */
        .lead-print { font-size: 11pt; line-height: 1.45; }
        .lead-print h1 { font-size: 18pt; }
        .lead-print h2, .lead-print h3 { font-size: 12pt; }

        /* Avoid orphaned section headers */
        .lead-print section { break-inside: avoid; }

        @page { margin: 14mm; size: A4; }
      }
    `}</style>
  );
}
